"use client";

import { Suspense, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import { PageHeader, WorkspacePage } from "@/components/ds/workspace-page";
import { AppHttpError } from "@/lib/api/errors";
import type { AlertRuleCreateDto } from "@/lib/api/types";
import { useAuthStore } from "@/modules/auth/store";

import { RuleBuilderForm } from "../components/rule-builder-form";
import { SemanticBatchRuleForm } from "../components/semantic-batch-rule-form";
import { useCreateAlertRule } from "../hooks";

function errorMessage(err: unknown): string {
  if (err instanceof AppHttpError) {
    return err.message;
  }
  if (err instanceof Error) {
    return err.message;
  }
  return "Request failed";
}

function RuleNewPageInner() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const tenantId = useAuthStore((s) => s.tenantId);
  const mutation = useCreateAlertRule();
  const [localError, setLocalError] = useState<string | null>(null);

  const batch = searchParams.get("batch") === "1";
  const defaults = useMemo(
    () => ({
      device_id: searchParams.get("device_id") ?? undefined,
      channel: searchParams.get("channel") ?? undefined,
      formula: searchParams.get("formula") ?? undefined,
      metric_id: searchParams.get("metric_id") ?? undefined,
      aggregation: searchParams.get("aggregation") ?? undefined,
      time_range: searchParams.get("time_range") ?? undefined,
    }),
    [searchParams],
  );

  const submitRule = (body: AlertRuleCreateDto) => {
    setLocalError(null);
    mutation.mutate(body, {
      onSuccess: () =>
        navigate("/alerts", {
          state: {
            toast: batch ? `Batch rule "${body.name}" created.` : `Alert rule "${body.name}" created.`,
          },
        }),
      onError: () => {},
    });
  };

  return (
    <WorkspacePage className="pt-0">
      <PageHeader
        kicker="Reliability"
        title={batch ? "Batch telemetry rule" : "Alert rule"}
        description={
          batch
            ? "Compose a semantic rule from dashboard context; submits to the alerts API."
            : "Define conditions and actions; persists as a classic alert rule."
        }
      />
      {batch && !tenantId ? (
        <p className="p-4 text-sm text-foreground/55">
          Sign in with a workspace-bound account to create batch telemetry rules.
        </p>
      ) : null}
      {batch && tenantId ? (
        <SemanticBatchRuleForm
          tenantId={tenantId}
          defaults={defaults}
          isSubmitting={mutation.isPending}
          errorText={localError ?? (mutation.isError ? errorMessage(mutation.error) : null)}
          onSubmit={(body) => submitRule(body)}
        />
      ) : null}
      {!batch ? (
        <RuleBuilderForm
          defaultTenantId={tenantId}
          isSubmitting={mutation.isPending}
          errorText={localError ?? (mutation.isError ? errorMessage(mutation.error) : null)}
          onSubmit={(body) => {
            setLocalError(null);
            if (!body.name) {
              setLocalError("Name is required");
              return;
            }
            submitRule({
              name: body.name,
              ...(body.tenant_id !== undefined ? { tenant_id: body.tenant_id } : {}),
              conditions: body.conditions,
            });
          }}
        />
      ) : null}
    </WorkspacePage>
  );
}

export function RuleNewPage() {
  return (
    <Suspense fallback={<p className="p-4 text-sm text-foreground/55">Loading…</p>}>
      <RuleNewPageInner />
    </Suspense>
  );
}
